import { ReactJSXElement } from "@storybook/theming/dist/ts3.9/_modules/@emotion-react-types-jsx-namespace";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export interface InjectedModelProps<
  ValueType = any,
  ParsedValueType = ValueType,
  FormattedValueType = ValueType,
> {
  parsedValue: ParsedValueType;
  formattedValue: FormattedValueType;
  [key: string]: any;
}

export interface IModel<
  ValueType = any,
  ParsedValueType = ValueType,
  FormattedValueType = ValueType,
> {
  /** Raw value */
  value: ValueType;
  children(
    props: InjectedModelProps<ValueType, ParsedValueType, FormattedValueType>,
  ): ReactJSXElement;
  onModelChange(
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
    parsedValue: ValueType,
  ): void;
  onChange?(
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ): void;
  /** Function that removes all unnecessary elements from the value ('cleans' the raw value) */
  parser?(value: ValueType): ParsedValueType;
  /** Function that formats current parsed value (not raw, the parsed one!) in a pretty way */
  formatter?(value: ValueType): FormattedValueType;
}

/** Parser / formatter for values
 * !!!Attention!!! There can be problems with passing the formatted value at some types of input (like, type="number") */
function Model({
  value,
  children,
  onChange,
  onModelChange,
  parser,
  formatter,
  ...rest
}: IModel) {
  const [parsedValue, setParsedValue] = useState(() =>
    parser ? parser(value) : value,
  );

  const valRef = useRef<IModel["value"]>("");
  useEffect(() => {
    if (valRef.current !== value) {
      valRef.current = value;
      const newValue = parser ? parser(value) : value;
      setParsedValue(newValue);
    }
  }, [parser, value]);

  const formattedValue = useMemo(
    () => (formatter ? formatter(parsedValue) : parsedValue),
    [formatter, parsedValue],
  );

  const changeHandler = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value: newValue } = event.target || { target: {} };
      const newParsedValue = parser ? parser(newValue) : newValue;
      setParsedValue(newParsedValue);
      onChange && onChange(event);
      onModelChange && onModelChange(event, newParsedValue);
    },
    [onChange, onModelChange, parser],
  );

  return children
    ? children({
        parsedValue,
        formattedValue,
        onChange: changeHandler,
        ...rest,
      })
    : null;
}

export default Model;
